import React, { useState } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectCrossClientAvailableNextStepCounts, selectCrossClientResultsExecution } from '../../../store/crossClient/crossClientResults'
import { CrossClientQueryEvaluationTypes, type CrossClientQueryEvaluationType } from '../../../models/CrossClient'
import { Button } from '../../../components/Button/Button'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { useCrossClientEvaluationActions } from './useCrossClientEvaluationActions'
import { crossClientIntegrationList } from './crossClientNextStep'
import { CROSS_CLIENT_AI_EVAL_BLOCKED_MESSAGE, CROSS_CLIENT_LOCAL_RESOLVER_BLOCKED_MESSAGE } from '../utils/crossClientFunds'
import { useCrossClientFundsQuotas } from '../utils/crossClientFundsQuotas'
import { pluralize } from '../utils'

const AI_BODY =
  "I'll gather everything across meetings, notes and integrations for each client and analyze it together. I'll pause after this many clients and notify you to review results."

const OPTION_CLASS_NAME = 'button-small rounded-3 text-nowrap zocks-border-0.5 zocks-border-color-gray-300'

type QuickActionKey = 'local' | 'ai-10' | 'ai-20' | 'ai-all'

export interface CrossClientQuickActionPopoverProps {
  onClose: () => void
}

export const CrossClientQuickActionPopover: React.FC<CrossClientQuickActionPopoverProps> = ({ onClose }) => {
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const { localNext, aiNext } = useAppSelector(selectCrossClientAvailableNextStepCounts)
  const { evaluateOutstandingByNextStep } = useCrossClientEvaluationActions()
  const { canAiEval, canLocalResolverEval, quotaLoading } = useCrossClientFundsQuotas()
  const [loadingKey, setLoadingKey] = useState<QuickActionKey | null>(null)

  const integrations = crossClientIntegrationList(execution)
  const isLoading = loadingKey !== null

  const run = async (key: QuickActionKey, type: CrossClientQueryEvaluationType, limit?: number) => {
    if (isLoading) {
      return
    }
    setLoadingKey(key)
    try {
      await evaluateOutstandingByNextStep(type, limit)
    } finally {
      setLoadingKey(null)
      onClose()
    }
  }

  return (
    <div className='d-flex flex-column'>
      {localNext > 0 ? (
        <div className='d-flex flex-column gap-2'>
          <div className='paragraph-medium fw-600 zocks-text-gray-900'>
            Check integrations for {localNext.toLocaleString()} {pluralize(localNext, 'client')}
          </div>
          <div className='paragraph-small zocks-text-gray-900'>I&apos;ll pull data from {integrations} for each client.</div>
          <div className='d-flex'>
            <Tooltip placement='top' title={canLocalResolverEval ? undefined : CROSS_CLIENT_LOCAL_RESOLVER_BLOCKED_MESSAGE}>
              <Button
                variant='header'
                className={OPTION_CLASS_NAME}
                disabled={isLoading || quotaLoading || !canLocalResolverEval}
                loading={loadingKey === 'local'}
                onClick={() => void run('local', CrossClientQueryEvaluationTypes.LOCAL_RESOLVER)}
              >
                Start
              </Button>
            </Tooltip>
          </div>
        </div>
      ) : null}
      {localNext > 0 && aiNext > 0 ? <hr className='my-3 zocks-border-color-gray-500' /> : null}
      {aiNext > 0 ? (
        <div className='d-flex flex-column gap-2'>
          <div className='paragraph-medium fw-600 zocks-text-gray-900'>
            Full review on {aiNext.toLocaleString()} {pluralize(aiNext, 'client')}
          </div>
          <div className='paragraph-small zocks-text-gray-900'>{AI_BODY}</div>
          <div className='d-flex flex-wrap gap-2'>
            {aiNext > 10 ? (
              <Tooltip placement='top' title={canAiEval ? undefined : CROSS_CLIENT_AI_EVAL_BLOCKED_MESSAGE}>
                <Button
                  variant='header'
                  className={OPTION_CLASS_NAME}
                  disabled={isLoading || quotaLoading || !canAiEval}
                  loading={loadingKey === 'ai-10'}
                  onClick={() => void run('ai-10', CrossClientQueryEvaluationTypes.AI, 10)}
                >
                  10 clients
                </Button>
              </Tooltip>
            ) : null}
            {aiNext > 20 ? (
              <Tooltip placement='top' title={canAiEval ? undefined : CROSS_CLIENT_AI_EVAL_BLOCKED_MESSAGE}>
                <Button
                  variant='header'
                  className={OPTION_CLASS_NAME}
                  disabled={isLoading || quotaLoading || !canAiEval}
                  loading={loadingKey === 'ai-20'}
                  onClick={() => void run('ai-20', CrossClientQueryEvaluationTypes.AI, 20)}
                >
                  20 clients
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip placement='top' title={canAiEval ? undefined : CROSS_CLIENT_AI_EVAL_BLOCKED_MESSAGE}>
              <Button
                variant='header'
                className={OPTION_CLASS_NAME}
                disabled={isLoading || quotaLoading || !canAiEval}
                loading={loadingKey === 'ai-all'}
                onClick={() => void run('ai-all', CrossClientQueryEvaluationTypes.AI)}
              >
                All {aiNext.toLocaleString()} {pluralize(aiNext, 'client')}
              </Button>
            </Tooltip>
          </div>
        </div>
      ) : null}
    </div>
  )
}
